import { PDFDocument, rgb, LineCapStyle, degrees, BlendMode } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import { MARKUP_TYPES, FILL_TYPES, TEXT_TYPES, fullStyle, calloutStart } from "./annotGeom";
import { col, colArr, pageMapper, toPath, makeFonts, drawTextT, wrap, bboxOfPts } from "./pdfCore";
import { drawBubble, bubbleDetails } from "./bubbleExport";
import { effBubbleStyle } from "./bubbleStyle";
import { createAnnotator } from "./pdfAnnots";
import { addPositionSummary } from "./pdfExport";

const ellipsePts = (x, y, w, h) => Array.from({ length: 49 }, (_, i) => {
    const a = (i / 48) * Math.PI * 2;
    return [x + w / 2 + (w / 2) * Math.cos(a), y + h / 2 + (h / 2) * Math.sin(a)];
});
function cloudPts(x, y, w, h, r) {
    const corners = [[x, y], [x + w, y], [x + w, y + h], [x, y + h], [x, y]];
    const out = [];
    for (let k = 0; k < 4; k++) {
        const [ax, ay] = corners[k], [bx, by] = corners[k + 1];
        const len = Math.hypot(bx - ax, by - ay), n = Math.max(1, Math.round(len / (r * 2)));
        const dx = (bx - ax) / len, dy = (by - ay) / len, nx = dy, ny = -dx, sr = len / n / 2;
        for (let i = 0; i < n; i++) {
            const cx = ax + dx * sr * (2 * i + 1), cy = ay + dy * sr * (2 * i + 1);
            for (let s = 0; s <= 8; s++) {
                const th = (s / 8) * Math.PI;
                out.push([cx - dx * sr * Math.cos(th) + nx * sr * Math.sin(th), cy - dy * sr * Math.cos(th) + ny * sr * Math.sin(th)]);
            }
        }
    }
    return out;
}

function shapeLines(c, Wv, Hv) {
    const g = c.geom || {};
    const B = () => [g.x * Wv, g.y * Hv, g.w * Wv, g.h * Hv];
    if (["rect", "comment", "callout", "text"].includes(c.type)) { const [x, y, w, h] = B(); return [[[x, y], [x + w, y], [x + w, y + h], [x, y + h], [x, y]]]; }
    if (c.type === "ellipse") return [ellipsePts(...B())];
    if (c.type === "cloud") return [cloudPts(...B(), 9)];
    if (c.type === "line" || c.type === "arrow") {
        const p1 = [g.x1 * Wv, g.y1 * Hv], p2 = [g.x2 * Wv, g.y2 * Hv];
        const lines = [[p1, p2]];
        if (c.type === "arrow") {
            const a = Math.atan2(p2[1] - p1[1], p2[0] - p1[0]), s = Math.max(10, (c.style?.width || 2) * 4);
            lines.push([[p2[0] - s * Math.cos(a - Math.PI / 7), p2[1] - s * Math.sin(a - Math.PI / 7)], p2, [p2[0] - s * Math.cos(a + Math.PI / 7), p2[1] - s * Math.sin(a + Math.PI / 7)]]);
        }
        return lines;
    }
    if (c.type === "draw") return [(g.points || []).map(([x, y]) => [x * Wv, y * Hv])];
    return [];
}

function calloutLeader(c, Wv, Hv) {
    const g = c.geom, s0 = calloutStart(g);
    const p1 = [s0.x * Wv, s0.y * Hv], p2 = [g.ax * Wv, g.ay * Hv];
    const a = Math.atan2(p2[1] - p1[1], p2[0] - p1[0]), s = Math.max(9, (c.style?.width || 2) * 3.5);
    return [[p1, p2], [[p2[0] - s * Math.cos(a - Math.PI / 7), p2[1] - s * Math.sin(a - Math.PI / 7)], p2, [p2[0] - s * Math.cos(a + Math.PI / 7), p2[1] - s * Math.sin(a + Math.PI / 7)]]];
}

async function imageForPdf(pdf, blob) {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    if (blob.type === "image/png") return pdf.embedPng(bytes);
    if (blob.type === "image/jpeg") return pdf.embedJpg(bytes);
    const bmp = await createImageBitmap(blob);
    const cv = document.createElement("canvas");
    cv.width = bmp.width; cv.height = bmp.height;
    cv.getContext("2d").drawImage(bmp, 0, 0);
    const png = await new Promise((r) => cv.toBlob(r, "image/png"));
    return pdf.embedPng(new Uint8Array(await png.arrayBuffer()));
}

const NOTE_S = 20;

// Draws one comment exactly like CommentLayer. Returns the PDF rect it covers.
async function drawComment(target, c, mapper, env, label) {
    const { Wv, Hv } = mapper;
    const pts = [];
    const map = (p) => { const r = mapper.map(p); pts.push(r); return r; };
    const st = fullStyle(c);
    const color = col(st.color);
    const bw = st.width || 2;
    if (c.type === "image") {
        const img = await imageForPdf(env.pdf, await env.fetchImage(c.image));
        const g = c.geom;
        const w = g.w * Wv, h = g.h * Hv;
        [[g.x, g.y], [g.x + g.w, g.y], [g.x, g.y + g.h], [g.x + g.w, g.y + g.h]].forEach(([x, y]) => map([x * Wv, y * Hv]));
        const [cx, cy] = mapper.map([(g.x + g.w / 2) * Wv, (g.y + g.h / 2) * Hv]);
        const phi = (-(g.rot || 0) * Math.PI) / 180;
        const x = cx - ((w / 2) * Math.cos(phi) - (h / 2) * Math.sin(phi));
        const y = cy - ((w / 2) * Math.sin(phi) + (h / 2) * Math.cos(phi));
        target.drawImage(img, { x, y, width: w, height: h, rotate: degrees((phi * 180) / Math.PI) });
        if (st.width > 0) target.drawRectangle({ x, y, width: w, height: h, rotate: degrees((phi * 180) / Math.PI), borderColor: color, borderWidth: st.width, borderOpacity: st.strokeOpacity });
        return bboxOfPts(pts, Math.max(2, Math.hypot(w, h) * 0.3 * (g.rot ? 1 : 0)));
    }
    let anchor;
    if (MARKUP_TYPES.includes(c.type)) {
        (c.geom.rects || []).forEach((r) => {
            const x = r.x * Wv, y = r.y * Hv, w = r.w * Wv, h = r.h * Hv;
            if (c.type === "highlight") target.drawSvgPath(toPath([[x, y], [x + w, y], [x + w, y + h], [x, y + h]], map, true), { x: 0, y: 0, color, opacity: 0.38, blendMode: BlendMode.Multiply });
            else {
                map([x, y]); map([x + w, y + h]);
                const ly = c.type === "underline" ? y + h * 0.95 : y + h / 2;
                target.drawSvgPath(toPath([[x, ly], [x + w, ly]], map), { x: 0, y: 0, borderColor: color, borderWidth: Math.max(0.8, h * 0.09) });
            }
            anchor = anchor || [x, y];
        });
    } else if (c.type === "note") {
        const x = c.geom.x * Wv, y = c.geom.y * Hv, s = NOTE_S;
        const P = (pp) => pp.map(([a, b]) => [x + a, y + b]);
        target.drawSvgPath(toPath(P([[0, 0], [s, 0], [s, s * 0.7], [s * 0.7, s], [0, s]]), map, true), { x: 0, y: 0, color: col(st.fill || "#f59e0b"), opacity: st.fillOpacity, borderColor: color, borderOpacity: st.strokeOpacity, borderWidth: 0.8 });
        target.drawSvgPath(toPath(P([[s, s * 0.7], [s * 0.7, s * 0.7], [s * 0.7, s]]), map, true), { x: 0, y: 0, color: rgb(0, 0, 0), opacity: 0.2 });
        [[5, s - 4], [8, s - 4], [11, s * 0.6]].forEach(([yy, x2]) => target.drawSvgPath(toPath(P([[4, yy], [x2, yy]]), map), { x: 0, y: 0, borderColor: col(st.textColor), borderWidth: 1.1 }));
        anchor = [x, y];
    } else {
        const outline = shapeLines(c, Wv, Hv);
        if (FILL_TYPES.includes(c.type) && st.fill && st.fillOpacity > 0 && outline[0]) {
            target.drawSvgPath(toPath(outline[0], map, true), { x: 0, y: 0, color: col(st.fill), opacity: st.fillOpacity });
        }
        [...outline, ...(c.type === "callout" ? calloutLeader(c, Wv, Hv) : [])].forEach((p) => {
            if (p.length > 1 && st.strokeOpacity > 0) target.drawSvgPath(toPath(p, map), { x: 0, y: 0, borderColor: color, borderOpacity: st.strokeOpacity, borderWidth: bw, borderLineCap: LineCapStyle.Round });
            else p.forEach(map);
        });
        if (TEXT_TYPES.includes(c.type) && c.text) {
            const boxed = ["comment", "callout", "text"].includes(c.type);
            const [x, y, w, h] = [c.geom.x * Wv, c.geom.y * Hv, c.geom.w * Wv, c.geom.h * Hv];
            const f = await env.fonts.get(st.fontFamily, st.bold, st.italic);
            const fs = st.fontSize || 12, lh = fs * 1.25;
            const lines = wrap(c.text, f, fs, w - 8);
            const align = st.align || (boxed ? "left" : "center");
            const top = boxed ? y + 4 : y + h / 2 - (lines.length * lh) / 2;
            const tc = col(st.textColor);
            lines.forEach((ln, i) => {
                const ty = top + i * lh + (lh - fs) / 2 + 0.8 * fs;
                if (ty > y + h + 1) return;
                const lw = f.widthOfTextAtSize(ln, fs);
                const lx = align === "center" ? x + (w - lw) / 2 : align === "right" ? x + w - 4 - lw : x + 4;
                drawTextT(target, ln, [lx, ty], [lx + 1, ty], mapper.map, { size: fs, font: f, color: tc });
                if (st.underline) {
                    const [a, b] = [mapper.map([lx, ty + fs * 0.12]), mapper.map([lx + lw, ty + fs * 0.12])];
                    target.drawLine({ start: { x: a[0], y: a[1] }, end: { x: b[0], y: b[1] }, thickness: Math.max(0.5, fs / 16), color: tc });
                }
            });
        }
        anchor = c.type === "text" ? null : outline[0]?.[0];
    }
    if (anchor && label) {
        const [ax, ay] = map([anchor[0], Math.max(8, anchor[1] - 3)]);
        target.drawText(label, { x: ax, y: ay, size: 8, font: env.bold, color: rgb(0.15, 0.39, 0.92) });
        map([anchor[0] + 18, Math.max(0, anchor[1] - 12)]);
    }
    return bboxOfPts(pts, bw / 2 + 2);
}

const SUBTYPE = { note: "Text", highlight: "Highlight", underline: "Underline", strike: "StrikeOut", line: "Line", arrow: "Line", draw: "Ink", ellipse: "Circle", rect: "Square", cloud: "Square", comment: "FreeText", text: "FreeText", callout: "FreeText" };

function annotExtra(c, mapper, st) {
    const { Wv, Hv, map } = mapper;
    const g = c.geom || {};
    const P = (fx, fy) => map([fx * Wv, fy * Hv]);
    const e = { BS: { W: st.width || 2, S: "S" } };
    if (FILL_TYPES.includes(c.type) && st.fill && st.fillOpacity > 0) e.IC = colArr(st.fill);
    if (c.type === "note") return { Name: "Comment" };
    if (MARKUP_TYPES.includes(c.type)) return { QuadPoints: (g.rects || []).flatMap((r) => [...P(r.x, r.y), ...P(r.x + r.w, r.y), ...P(r.x, r.y + r.h), ...P(r.x + r.w, r.y + r.h)]) };
    if (c.type === "line" || c.type === "arrow") return { ...e, L: [...P(g.x1, g.y1), ...P(g.x2, g.y2)], LE: ["None", c.type === "arrow" ? "OpenArrow" : "None"] };
    if (c.type === "draw") return { ...e, InkList: [(g.points || []).flatMap(([x, y]) => P(x, y))] };
    if (c.type === "cloud") return { ...e, BE: { S: "C", I: 1 } };
    if (SUBTYPE[c.type] === "FreeText") {
        const tc = colArr(st.textColor).map((v) => v.toFixed(3)).join(" ");
        const ft = { ...e, DA: `/Helv ${st.fontSize || 12} Tf ${tc} rg`, Q: { left: 0, center: 1, right: 2 }[st.align || "left"] };
        if (c.type === "callout") { const s0 = calloutStart(g); return { ...ft, IT: "FreeTextCallout", CL: [...P(g.ax, g.ay), ...P(s0.x, s0.y)], LE: "OpenArrow" }; }
        return ft;
    }
    return e;
}

const TEXTUAL = ["note", "comment", "callout", "text"];
export function filterComments(comments, content) {
    if (content === "none") return [];
    if (content === "markup") return comments.filter((c) => !TEXTUAL.includes(c.type)).map((c) => ({ ...c, text: "", replies: [] }));
    if (content === "resolved") return comments.filter((c) => c.resolved || c.type === "image" || c.type === "text");
    return comments;
}

function addCommentList(pdf, list, font, bold, projectName, t, dateFmt) {
    let sp = pdf.addPage([595, 842]);
    let y = 800;
    const line = (txt, f, size, color = rgb(0.12, 0.12, 0.12), indent = 0) => {
        wrap(txt, f, size, 515 - indent).forEach((ln) => {
            if (y < 50) { sp = pdf.addPage([595, 842]); y = 800; }
            sp.drawText(ln, { x: 40 + indent, y, size, font: f, color });
            y -= size + 4;
        });
    };
    line(`${t("cm_summary_title")} – ${projectName || ""}`, bold, 16);
    y -= 8;
    if (!list.some((c) => c.type !== "image")) line(t("cm_empty"), font, 10);
    list.forEach((c, idx) => {
        if (c.type === "image") return;
        const status = c.resolved ? t("cm_resolved") : t("cm_open");
        line(`#${idx + 1} · ${t("pv_page", { n: c.page })} · ${t(`cm_${c.type}`)} · ${c.author?.name || ""} · ${dateFmt(c.created_at)} · ${status}`, bold, 9.5);
        if (c.text) line(c.text, font, 10, rgb(0.15, 0.15, 0.15), 10);
        (c.replies || []).forEach((r) => line(`– ${r.author?.name || ""} (${dateFmt(r.created_at)}): ${r.text}`, font, 9.5, rgb(0.3, 0.3, 0.3), 22));
        y -= 6;
    });
}

// method: "flatten" | "annot" | "both". content: "all" | "markup" | "none" | "resolved".
export async function buildExportPdf(originalBytes, o) {
    const { comments = [], content = "all", method = "both", includeBubbles = true, includeSummary = false, units = [], tray = {},
        unitOffsets = {}, bubbleStyles = {}, rows = [], columns = [], projectName, author, t, dateFmt, fetchImage } = o;
    const pdf = await PDFDocument.load(originalBytes);
    pdf.registerFontkit(fontkit);
    const fonts = makeFonts(pdf);
    const env = { pdf, fonts, fetchImage, font: await fonts.get("sans"), bold: await fonts.get("sans", true) };
    const ann = createAnnotator(pdf);
    const pages = pdf.getPages();
    const flat = method !== "annot", native = method !== "flatten";

    const place = async (page, draw, annot) => {
        let rect, ap;
        if (flat) rect = await draw(page);
        if (!native) return;
        if (flat) ap = ann.emptyAP(rect);
        else ({ ref: ap, rect } = await ann.apFrom(page, draw));
        ann.addMarkup(page, { ...annot, rect, ap });
    };

    if (includeBubbles) {
        const rowById = Object.fromEntries(rows.map((r) => [r.id, r]));
        for (const u of units) {
            const off = unitOffsets[u.unitId];
            const page = off && pages[(off.page || 1) - 1];
            if (!page) continue;
            const mp = pageMapper(page);
            const st = effBubbleStyle(tray, bubbleStyles, u.unitId);
            const bu = { ...u, cx: off.fx * mp.Wv, cy: off.fy * mp.Hv, base: mp.Wv * 0.03 };
            await place(page, (tg) => drawBubble(tg, bu, st, mp.map, fonts), {
                subtype: "Square", id: u.unitId, author, subject: t("ex_pos_subject"), contents: bubbleDetails(u, rowById[u.row_id], columns),
                color: colArr(st.bg || u.color),
            });
        }
    }

    const list = filterComments(comments, content).sort((a, b) => a.page - b.page || a.created_at.localeCompare(b.created_at));
    for (const [idx, c] of list.entries()) {
        const page = pages[c.page - 1];
        if (!page) continue;
        const mp = pageMapper(page);
        const label = includeSummary ? `#${idx + 1}` : null;
        if (c.type === "image") {
            if (!fetchImage || !c.image) continue;
            try { await drawComment(page, c, mp, env, null); } catch { /* skip broken image */ }
            continue;
        }
        const st = fullStyle(c);
        await place(page, (tg) => drawComment(tg, c, mp, env, label), {
            subtype: SUBTYPE[c.type] || "Square", id: c.id, author: c.author?.name, date: c.created_at, contents: c.text || "",
            subject: t(`cm_${c.type}`), color: c.type === "note" ? colArr(st.fill || st.color) : colArr(st.color),
            open: c.type === "note" && !!c.text, replies: c.replies, resolved: c.resolved, resolvedText: t("cm_resolved"),
            extra: annotExtra(c, mp, st),
        });
    }

    if (includeSummary) {
        if (includeBubbles && rows.length) addPositionSummary(pdf, rows, env.font, env.bold, projectName);
        if (list.length) addCommentList(pdf, list, env.font, env.bold, projectName, t, dateFmt);
    }
    return await pdf.save();
}
